// Cash Flow Forecasting Agent UI (Polished)
// Additions: horizon line chart (projected balance), AR/AP area chart,
// scenario comparison, summary strip, alert cards.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Chip, Stack, Paper, Grid, Divider,
    FormControlLabel, Switch,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, TrendChart, SimpleBarChart, fmtMoney, fmtDate, CHART_COLORS,
} from './AgentUIKit';
import {
    cfForecast, cfArPredict, cfApSchedule, cfScenarios, cfAlerts, cfNarrative,
    financialOperationsSummary,
} from './api';

const TABS = ['Forecast', 'AR Collections', 'AP Schedule', 'Scenarios', 'Alerts', 'Narrative'];
// Accent for SummaryGrid cards (maps to MetricCard palette)
const alertAccent = (a) => a === 'EMERGENCY' || a === 'CRITICAL' ? 'danger' : a === 'WARNING' ? 'warning' : 'neutral';
// Color for MUI <Chip> (must be one of: default|primary|secondary|error|info|success|warning)
const alertChipColor = (a) => a === 'EMERGENCY' || a === 'CRITICAL' ? 'error' : a === 'WARNING' ? 'warning' : 'default';

export default function CashFlowForecastingAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useAi, setUseAi] = useState(true);
    const [summary, setSummary] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [minBal, setMinBal] = useState(10000);
    const [arPredict, setArPredict] = useState(null);
    const [apSchedule, setApSchedule] = useState(null);
    const [scenarios, setScenarios] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [narrative, setNarrative] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        financialOperationsSummary().then(r => r?.data?.success && setSummary(r.data.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !forecast) { const r = await run(() => cfForecast(minBal)); if (r?.data?.success) setForecast(r.data.data); }
            else if (tab === 1 && !arPredict) { const r = await run(() => cfArPredict()); if (r?.data?.success) setArPredict(r.data.data); }
            else if (tab === 2 && !apSchedule) { const r = await run(() => cfApSchedule(60)); if (r?.data?.success) setApSchedule(r.data.data); }
            else if (tab === 3 && !scenarios) { const r = await run(() => cfScenarios(30)); if (r?.data?.success) setScenarios(r.data.data); }
            else if (tab === 4 && !alerts) { const r = await run(() => cfAlerts(minBal)); if (r?.data?.success) setAlerts(r.data.data); }
            else if (tab === 5 && !narrative) { const r = await run(() => cfNarrative(7, useAi)); if (r?.data?.success) setNarrative(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Current Cash', value: fmtMoney(summary?.cashflow?.current_cash ?? forecast?.forecasts?.[0]?.current_balance), accent: 'primary' },
        { label: 'AR Outstanding', value: fmtMoney(summary?.cashflow?.ar_outstanding ?? arPredict?.total_expected), accent: 'success' },
        { label: 'AP 30d Outflow', value: fmtMoney(summary?.cashflow?.ap_outflow_30d ?? apSchedule?.total_scheduled), accent: 'warning' },
        { label: 'Active Alerts', value: alerts?.alert_count ?? '—', accent: alerts?.alert_count > 0 ? 'danger' : 'success' },
    ];

    // Forecast chart data
    const forecastChartData = (forecast?.forecasts || []).map(f => ({
        horizon: `${f.horizon_days}d`,
        date: f.horizon_date,
        projected: f.projected_balance,
        inflow: f.ar_expected_inflow,
        outflow: f.ap_expected_outflow,
    }));

    const scenarioChartData = scenarios?.scenarios ? Object.entries(scenarios.scenarios).map(([k, v]) => ({
        scenario: k.replace(/_/g, ' '),
        projected: v.projected,
        probability: v.probability,
    })) : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return forecast ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Multi-Horizon Cash Forecast"
                            subtitle="Projected cash position at 7/30/60/90 day horizons"
                            actions={
                                <Stack direction="row" spacing={1}>
                                    <TextField size="small" label="Min balance" type="number" value={minBal}
                                        onChange={e => setMinBal(parseFloat(e.target.value) || 0)} sx={{ width: 140 }} />
                                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                                        onClick={async () => {
                                            setForecast(null);
                                            const r = await run(() => cfForecast(minBal));
                                            if (r?.data?.success) setForecast(r.data.data);
                                        }}>Refresh</Button>
                                </Stack>
                            }
                        />
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" fontWeight={600}>Projected Balance Over Horizons</Typography>
                            <TrendChart
                                data={forecastChartData}
                                xKey="horizon"
                                series={[{ key: 'projected', label: 'Projected Balance', color: CHART_COLORS.primary }]}
                                height={260}
                                yFormatter={v => fmtMoney(v)}
                                type="area"
                            />
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" fontWeight={600}>AR Inflow vs AP Outflow</Typography>
                            <SimpleBarChart
                                data={forecastChartData}
                                xKey="horizon"
                                series={[
                                    { key: 'inflow', label: 'AR Inflow', color: CHART_COLORS.success },
                                    { key: 'outflow', label: 'AP Outflow', color: CHART_COLORS.danger },
                                ]}
                                height={240}
                                yFormatter={v => fmtMoney(v)}
                            />
                        </Paper>
                        <DrillTable
                            columns={[
                                { key: 'horizon_days', label: 'Horizon', render: r => `${r.horizon_days}d` },
                                { key: 'horizon_date', label: 'Date' },
                                { key: 'current_balance', label: 'Current', align: 'right', render: r => fmtMoney(r.current_balance) },
                                { key: 'ar_expected_inflow', label: 'AR In', align: 'right', render: r => fmtMoney(r.ar_expected_inflow) },
                                { key: 'ap_expected_outflow', label: 'AP Out', align: 'right', render: r => fmtMoney(r.ap_expected_outflow) },
                                { key: 'projected_balance', label: 'Projected', align: 'right', render: r => <b>{fmtMoney(r.projected_balance)}</b> },
                                { key: 'alert', label: 'Alert', render: r => r.alert && <Chip size="small" color={alertChipColor(r.alert)} label={r.alert} /> },
                            ]}
                            rows={forecast.forecasts || []}
                            rowKeyField="horizon_days"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 1:
                return arPredict ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="AR Collection Predictor" subtitle="Per-invoice payment date predictions based on customer DSO history" />
                        <SummaryGrid cards={[
                            { label: 'Open Invoices', value: arPredict.count, accent: 'primary' },
                            { label: 'Total Expected', value: fmtMoney(arPredict.total_expected), accent: 'success' },
                        ]} cols={2} />
                        <DrillTable
                            columns={[
                                { key: 'invoice_id', label: 'Invoice' },
                                { key: 'customer_id', label: 'Customer', render: r => r.customer_id || '-' },
                                { key: 'balance_due', label: 'Balance', align: 'right', render: r => fmtMoney(r.balance_due) },
                                { key: 'due_date', label: 'Due' },
                                { key: 'predicted_payment_date', label: 'Predicted' },
                                { key: 'predicted_dso_days', label: 'DSO', align: 'right' },
                                { key: 'status', label: 'Status', render: r => r.is_overdue
                                    ? <Chip size="small" color="error" label={`${r.days_overdue}d overdue`} />
                                    : <Chip size="small" label={`${(r.confidence * 100).toFixed(0)}%`} /> },
                            ]}
                            rows={arPredict.predictions || []}
                            rowKeyField="invoice_id"
                            maxHeight={480}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return apSchedule ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="AP Outflow Scheduler" subtitle="Upcoming payment outflows by due date" />
                        <SummaryGrid cards={[
                            { label: 'Total Scheduled', value: fmtMoney(apSchedule.total_scheduled), accent: 'warning' },
                            { label: 'Days w/ Outflows', value: apSchedule.days_with_outflows, accent: 'info' },
                            { label: 'Peak Day', value: apSchedule.peak_outflow_day?.due_date || '—', accent: 'danger' },
                            { label: 'Peak Amount', value: fmtMoney(apSchedule.peak_outflow_day?.total_outflow), accent: 'danger' },
                        ]} />
                        {(apSchedule.schedule || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Outflow Timeline</Typography>
                                <SimpleBarChart
                                    data={apSchedule.schedule}
                                    xKey="due_date"
                                    series={[{ key: 'total_outflow', label: 'Outflow', color: CHART_COLORS.warning }]}
                                    height={240}
                                    yFormatter={v => fmtMoney(v)}
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'due_date', label: 'Due Date' },
                                { key: 'bill_count', label: 'Bills', align: 'right' },
                                { key: 'total_outflow', label: 'Total', align: 'right', render: r => fmtMoney(r.total_outflow) },
                                { key: 'unique_vendors', label: 'Vendors', align: 'right' },
                            ]}
                            rows={apSchedule.schedule || []}
                            rowKeyField="due_date"
                            maxHeight={480}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 3:
                return scenarios && !scenarios.error ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Scenario Modeling" subtitle="Best/worst/most-likely cash position with stress test" />
                        <SummaryGrid cards={[
                            { label: 'Probability-Weighted', value: fmtMoney(scenarios.probability_weighted_expected), accent: 'info' },
                            { label: 'Stress Test', value: fmtMoney(scenarios.stress_test_projection), accent: 'danger' },
                        ]} cols={2} />
                        {scenarioChartData.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Scenario Comparison</Typography>
                                <SimpleBarChart
                                    data={scenarioChartData}
                                    xKey="scenario"
                                    series={[{ key: 'projected', label: 'Projected', color: CHART_COLORS.primary }]}
                                    height={240}
                                    yFormatter={v => fmtMoney(v)}
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <Grid container spacing={1}>
                            {Object.entries(scenarios.scenarios || {}).map(([k, v]) => (
                                <Grid key={k} item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                                            {k.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{fmtMoney(v.projected)}</Typography>
                                        <Typography variant="caption" color="text.secondary">{v.assumption}</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip size="small" label={`P=${(v.probability * 100).toFixed(0)}%`} />
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: 'error.main', bgcolor: 'rgba(213,43,30,0.05)' }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <WarningAmberIcon sx={{ fontSize: 18, color: 'error.main' }} />
                                <Typography variant="caption" fontWeight={600} color="error.main">Stress Test Assumption</Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>{scenarios.stress_test_assumption}</Typography>
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 4:
                return alerts ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Liquidity Alerts" subtitle="Proactive warnings on cash shortfalls" />
                        {alerts.alert_count === 0 ? (
                            <EmptyState message="No liquidity alerts — projected cash stays above minimum." />
                        ) : (
                            <>
                                <DrillTable
                                    columns={[
                                        { key: 'horizon_days', label: 'Horizon', render: r => `${r.horizon_days}d (${r.horizon_date})` },
                                        { key: 'projected_balance', label: 'Projected', align: 'right', render: r => fmtMoney(r.projected_balance) },
                                        { key: 'alert', label: 'Alert', render: r => <Chip size="small" color={alertChipColor(r.alert)} label={r.alert} /> },
                                    ]}
                                    rows={alerts.alerts}
                                    rowKeyField="horizon_days"
                                />
                                {(alerts.recommended_actions || []).length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Recommended Actions</Typography>
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                            {alerts.recommended_actions.map((a, i) => (
                                                <Typography key={i} variant="body2">• {a}</Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}
                            </>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return narrative ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Cash Flow Narrative"
                            subtitle="AI-generated weekly cash flow explanation"
                            actions={
                                <Stack direction="row" spacing={1}>
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI</Typography>}
                                    />
                                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                                        onClick={async () => { setNarrative(null); const r = await run(() => cfNarrative(7, useAi)); if (r?.data?.success) setNarrative(r.data.data); }}>Refresh</Button>
                                </Stack>
                            }
                        />
                        <SummaryGrid cards={[
                            { label: 'Inflow', value: fmtMoney(narrative.inflow), accent: 'success' },
                            { label: 'Outflow', value: fmtMoney(narrative.outflow), accent: 'danger' },
                            { label: 'Net', value: fmtMoney(narrative.net), accent: narrative.net >= 0 ? 'success' : 'warning' },
                            { label: 'Transactions', value: narrative.transaction_count, accent: 'info' },
                        ]} />
                        <AIAnalysisBox text={narrative.ai_narrative} title="AI Executive Briefing" />
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" fontWeight={600}>Basic Summary</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>{narrative.narrative}</Typography>
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Cash Flow Forecasting Agent"
            subtitle="Agentic Financial Operations — multi-horizon forecast, AR/AP scheduling, scenario modeling, AI liquidity narrative."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
